import { Handle, Position } from '@xyflow/react';
import { User, Database } from 'lucide-react';

// ============================================================================
// ProcessNode
// ============================================================================

const domainBorderColor: Record<string, string> = {
  intake: '#60a5fa',      // blue-400
  shaping: '#a78bfa',     // violet-400
  cycle: '#6366f1',       // indigo-500
  governance: '#f59e0b',  // amber-500
  output: '#10b981',      // emerald-500
};

interface ProcessNodeData {
  label: string;
  description?: string;
  domain?: string;
}

export function ProcessNode({ data }: { data: ProcessNodeData }) {
  const borderColor = data.domain ? (domainBorderColor[data.domain] ?? '#d1d5db') : '#d1d5db';

  return (
    <div
      style={{
        width: 200,
        borderLeft: `4px solid ${borderColor}`,
        background: '#fff',
        borderRadius: '0.75rem',
        boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
        border: `1px solid #e5e7eb`,
        borderLeftColor: borderColor,
        borderLeftWidth: 4,
        padding: '10px 12px',
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: '#9ca3af' }} />
      <Handle type="target" position={Position.Top} style={{ background: '#9ca3af' }} />
      <Handle type="source" position={Position.Right} style={{ background: '#9ca3af' }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#9ca3af' }} />
      <p style={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827', margin: 0 }}>
        {data.label}
      </p>
      {data.description && (
        <p
          style={{
            fontSize: '0.75rem',
            color: '#6b7280',
            marginTop: 2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {data.description}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// ActorNode
// ============================================================================

interface ActorNodeData {
  label: string;
  description?: string;
}

export function ActorNode({ data }: { data: ActorNodeData }) {
  return (
    <div
      style={{
        width: 160,
        background: '#f9fafb',
        border: '2px dashed #d1d5db',
        borderRadius: '0.75rem',
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: '#9ca3af' }} />
      <Handle type="target" position={Position.Top} style={{ background: '#9ca3af' }} />
      <Handle type="source" position={Position.Right} style={{ background: '#9ca3af' }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#9ca3af' }} />
      <User size={16} color="#6b7280" style={{ flexShrink: 0 }} />
      <div>
        <p style={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827', margin: 0 }}>
          {data.label}
        </p>
        {data.description && (
          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {data.description}
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// DataNode
// ============================================================================

interface DataNodeData {
  label: string;
  description?: string;
}

export function DataNode({ data }: { data: DataNodeData }) {
  return (
    <div
      style={{
        width: 180,
        background: '#f0f9ff',
        border: '1px solid #bae6fd',
        borderRadius: '0.5rem',
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: '#9ca3af' }} />
      <Handle type="target" position={Position.Top} style={{ background: '#9ca3af' }} />
      <Handle type="source" position={Position.Right} style={{ background: '#9ca3af' }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#9ca3af' }} />
      <Database size={16} color="#0284c7" style={{ flexShrink: 0, marginTop: 1 }} />
      <div>
        <p style={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827', margin: 0 }}>
          {data.label}
        </p>
        {data.description && (
          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {data.description}
          </p>
        )}
      </div>
    </div>
  );
}
